// Study Hall layout (DESIGN.md 16.2 and 16.3): the room rail (a <select> under 640 px), the room
// header, banners, the message log with date separators, grouping, reply previews, reaction chips,
// the unread divider and the "N new messages" pill, and the one hover/focus toolbar that moves to
// whichever row is active. Every user string (bodies, handles, topics, reasons) goes through
// textContent or markdown.toDom; nothing here parses HTML (ESLint enforces it for src/client/hall).
import * as C from "./copy.js";
import { parse, toDom, plain } from "./markdown.js";
import { groupable, dayKey, hue, clock } from "./store.js";
import { trustStrip } from "../shared/privacy-copy.js";

const NS = "http://www.w3.org/2000/svg";

// Inside Re:Vault, /privacy opens as a tab of its own (the app checks the address); on its own,
// the link just navigates.
function openPage(url) {
	if (window.parent !== window) {
		try { window.parent.postMessage({ type: "revault-open", url }, location.origin); return; } catch (e) { void e; }
	}
	location.href = url;
}
export const trustLine = () => trustStrip(document, "rv-trust rv-hall-trust", openPage);
export const REPORT_DELETED_MS = 7 * 86400000;
const ICONS = {
	hall: "M4 5h11a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9l-4 3v-3H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM17 9h3a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-1v3l-4-3h-3",
	reply: "M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z",
	react: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01",
	edit: "M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z",
	del: "M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6",
	report: "M4 22V4M4 4h13l-2 4 2 4H4",
	block: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM5.6 5.6l12.8 12.8",
	unblock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9 12l2 2 4-4",
	shield: "M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z",
	user: "M20 21a8 8 0 0 0-16 0M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
	remove: "M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6 0 10 7 10 7a17 17 0 0 1-3 3.7M6.6 6.6C3.9 8.4 2 12 2 12s4 7 10 7a9.7 9.7 0 0 0 5.4-1.6M9.9 9.9a3 3 0 0 0 4.2 4.2",
	restore: "M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5",
	approve: "M20 6 9 17l-5-5",
	timeout: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2",
	ban: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9 9l6 6M15 9l-6 6",
	plus: "M12 5v14M5 12h14",
	close: "M6 6l12 12M18 6 6 18",
	send: "M4 12 20 4l-6 16-3-7z",
	lock: "M6 11h12v10H6zM8 11V7a4 4 0 0 1 8 0v4",
	more: "M5 12h.01M12 12h.01M19 12h.01",
	poll: "M4 20V10M10 20V4M16 20v-7M22 20H2",
	invite: "M15 19a6 6 0 0 0-12 0M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM19 8v6M22 11h-6",
	focus: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2",
	menu: "M4 6h16M4 12h16M4 18h16",
	tool: "M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-.6-.6-2.4z",
	link: "M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1",
	pin: "M9 4h6l-1 6 3 3H7l3-3zM12 13v8",
	chevron: "M9 6l6 6-6 6",
	// The six reactions, drawn so they look the same on every device (no emoji font needed).
	rx0: "M7 11v9H4v-9zM7 11l4-7c1.5 0 2.5 1 2.2 2.6L12.6 10H18a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 16.8 20H7",
	rx1: "M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z",
	rx2: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 13.5c1 2 2.4 3 4 3s3-1 4-3zM8 9.5l2 1-2 1M16 9.5l-2 1 2 1",
	rx3: "M4 20l4.5-11 6.5 6.5zM14 4v2M18.5 5.5l-1.5 1.5M20 10h-2M11 7l1 1M16 12l1 1",
	rx4: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9 15h6M9 9.5h.01M15 9.5h.01M14 6.5l2 1",
	rx5: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 12.5l2.7 2.7L16 9.8",
};

// createElement with props: class, text (textContent), on<event>, hidden, value, else attributes.
export function h(tag, props, ...kids) {
	const e = document.createElement(tag);
	if (props) {
		for (const [k, v] of Object.entries(props)) {
			if (v === undefined || v === null || v === false) continue;
			if (k === "class") e.className = v;
			else if (k === "text") e.textContent = String(v);
			else if (k === "hidden") e.hidden = true;
			// Through the CSSOM: Study Hall's CSP allows no style attributes.
			else if (k === "style") e.style.cssText = String(v);

			else if (k === "value") e.value = v;
			else if (k.startsWith("on") && typeof v === "function") e.addEventListener(k.slice(2), v);
			else e.setAttribute(k, v === true ? "" : String(v));
		}
	}
	for (const c of kids.flat(Infinity)) if (c !== null && c !== undefined && c !== false) e.append(c.nodeType ? c : String(c));
	return e;
}

export function icon(name) {
	const s = document.createElementNS(NS, "svg");
	for (const [k, v] of [["viewBox", "0 0 24 24"], ["fill", "none"], ["stroke", "currentColor"], ["stroke-width", "1.6"], ["stroke-linecap", "round"], ["stroke-linejoin", "round"], ["aria-hidden", "true"], ["focusable", "false"]]) s.setAttribute(k, v);
	const p = document.createElementNS(NS, "path");
	p.setAttribute("d", ICONS[name] || "");
	s.appendChild(p);
	return s;
}

export const fmtTime = (at) => new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
export const fmtFull = (at) => new Date(at).toLocaleString([], { dateStyle: "full", timeStyle: "short" });
// "3:40 PM" today, "Tue 3:40 PM" within a week, else the date as well (timeouts and lockdowns).
export function fmtUntil(at, now = Date.now()) {
	if (!Number.isFinite(at) || at <= 0) return "";
	if (dayKey(at) === dayKey(now)) return fmtTime(at);
	if (at - now < 6 * 86400000) return new Date(at).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });
	return new Date(at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}
export function dayLabel(at, now = Date.now()) {
	if (dayKey(at) === dayKey(now)) return C.TODAY;
	if (dayKey(at) === dayKey(now - 86400000)) return C.YESTERDAY;
	return new Date(at).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
}
export const badgeFor = (s) => (s === 1 ? "Mod" : s >= 2 ? "Team" : "");
// Supporter flair "<badge>|<color>|<flair>" (docs/SUPPORTERS.md 8): opt-in, set by the server.
const SUP = { supporter: "Supporter", patron: "Patron", founding: "Founding supporter" };
const FL = { star: "\u2605", book: "\u2761", leaf: "\u2766", moon: "\u263e", note: "\u266a", spark: "\u2726", pencil: "\u270e", flask: "\u2697" };
export const flairOf = (f) => { const [b, c, g] = String(f || "").split("|"); return SUP[b] ? { name: SUP[b], b, c: /^[0-7]$/.test(c) ? c : null, g: FL[g] || "" } : null; };
const monogram = (hd) => (Array.from(hd.trim())[0] || "?").toUpperCase();

// ctx: { store, act(verb, m), join(room), more(), jumpLatest(), onScroll(atBottom), toggleMod(),
//        openMe(), openRules(), composer: element, lite() }
export function createView(root, ctx) {
	const { store } = ctx;
	const S = store.S;
	const rowEls = new Map();
	const revealed = new Set(); // blocked rows the viewer chose to show
	let els = null, mark = 0, markRoom = "", newCount = 0, toolRow = null, picker = null, toastT = 0, lastFirst = 0, archivedOpen = false;

	// --- page states (16.2) -----------------------------------------------------------------
	function card({ title, text, button, onClick, extra, busy }) {
		els = null;
		rowEls.clear();
		root.textContent = "";
		root.dataset.state = "card";
		const box = h("section", { class: "rv-hall-card", "aria-busy": busy ? "true" : null },
			h("div", { class: "rv-hall-card-icon" }, icon("hall")),
			h("h1", { text: title || C.NAME }),
			text ? h("p", { text }) : null,
			extra || null,
			button ? h("button", { class: "rv-btn rv-btn-primary", text: button, onclick: onClick }) : null);
		root.append(box);
		return box;
	}
	function loading() {
		els = null;
		rowEls.clear();
		root.textContent = "";
		root.dataset.state = "loading";
		const rail = h("aside", { class: "rv-hall-rail rv-hall-skel", "aria-hidden": "true" }, [1, 2, 3, 4, 5, 6].map(() => h("span", { class: "rv-hall-skel-line" })));
		root.append(rail, h("section", { class: "rv-hall-main" }, h("p", { class: "rv-hall-connecting", role: "status", text: C.CONNECTING })));
	}

	// --- main layout (16.3) ------------------------------------------------------------------
	function mount() {
		if (els) return els;
		pinsKey = ""; introKey = "";

		root.textContent = "";
		root.dataset.state = "main";
		const e = {};
		e.rooms = h("ul", { class: "rv-hall-rooms" });
		e.me = h("button", { class: "rv-hall-me", type: "button", onclick: ctx.openMe });
		e.rail = h("aside", { class: "rv-hall-rail" },
			h("div", { class: "rv-hall-brand" }, icon("hall"), h("span", { text: C.NAME })),
			e.me,
			h("nav", { "aria-label": C.ROOMS }, e.rooms),
			h("div", { class: "rv-hall-rail-foot" },
				h("button", { type: "button", class: "rv-hall-link", text: C.RULES_BUTTON, onclick: ctx.openRules }),
				(e.density = h("button", { type: "button", class: "rv-hall-link", onclick: ctx.toggleDensity }))),
			trustLine());
		e.select = h("select", { class: "rv-hall-room-select", "aria-label": C.ROOMS, onchange: () => ctx.join(e.select.value) });
		e.title = h("h1", { class: "rv-hall-title" });
		e.topic = h("span", { class: "rv-hall-topic" });
		e.here = h("span", { class: "rv-hall-tag" });
		e.slow = h("span", { class: "rv-hall-tag" });
		e.lock = h("span", { class: "rv-hall-tag" }, icon("lock"), C.LOCKED_TAG);
		e.shield = h("button", { type: "button", class: "rv-hall-icon-btn rv-hall-shield", "aria-label": C.MOD.title, title: C.MOD.title, onclick: ctx.toggleMod }, icon("shield"), (e.modq = h("span", { class: "rv-hall-badge-n" })));
		// Under 640 px the rail is hidden: the name/rules menu moves into the header.
		e.meMini = h("button", { type: "button", class: "rv-hall-icon-btn rv-hall-memini", "aria-label": C.CHANGE_NAME, title: C.CHANGE_NAME, onclick: ctx.openMe }, icon("user"));
		// Under 640 px the rail's links (Rules, density) live in this menu.
		e.menuMini = h("button", { type: "button", class: "rv-hall-icon-btn rv-hall-memini", "aria-label": C.MENU, title: C.MENU, "aria-haspopup": "dialog", onclick: ctx.openMenu }, icon("menu"));
		e.pollBtn = h("button", { type: "button", class: "rv-hall-icon-btn", "aria-label": C.NEW_POLL, title: C.NEW_POLL, onclick: ctx.newPoll }, icon("poll"));
		e.invBtn = h("button", { type: "button", class: "rv-hall-icon-btn", "aria-label": C.INVITE, title: C.INVITE, "aria-haspopup": "dialog", onclick: () => ctx.invite(e.invBtn) }, icon("invite"));
		e.head = h("header", { class: "rv-hall-head" }, e.select, h("div", { class: "rv-hall-head-text" }, e.title, e.topic), h("div", { class: "rv-hall-tags" }, e.here, e.slow, e.lock), e.invBtn, e.pollBtn, e.shield, e.meMini, e.menuMini);
		// The room's shared focus timer (and Hangout's "next break" line), under the header.
		e.ring = h("span", { class: "rv-hall-ring", "aria-hidden": "true" });
		e.tlabel = h("span", { class: "rv-hall-tlabel", role: "timer", "aria-live": "off" });
		e.tcount = h("span", { class: "rv-hall-tcount" });
		e.tbtn = h("button", { type: "button", class: "rv-btn rv-hall-tbtn", onclick: () => ctx.timerAct(e.tbtn.dataset.act) });
		e.tstop = h("button", { type: "button", class: "rv-hall-link rv-hall-tstop", text: C.STAFF_STOP, onclick: () => ctx.timerAct("stop") });
		// Room sound: a real switch, off until you turn it on, remembered on this device.
		e.sound = h("input", { type: "checkbox", role: "switch", class: "rv-hall-toggle", onchange: () => ctx.setSound(e.sound.checked) });
		e.soundWrap = h("label", { class: "rv-hall-sound", title: C.ROOM_SOUND_HINT }, e.sound, h("span", { text: C.ROOM_SOUND }));
		e.rule = h("p", { class: "rv-hall-rule" });
		e.timer = h("div", { class: "rv-hall-timer", hidden: true }, e.ring, h("div", { class: "rv-hall-ttext" }, e.tlabel, e.tcount), e.tbtn, e.soundWrap, e.tstop);
		e.pinsList = h("ul", { class: "rv-hall-pinlist" });
		e.pins = h("section", { class: "rv-hall-pins", "aria-labelledby": "rv-hall-pins-h", hidden: true }, h("h2", { id: "rv-hall-pins-h", class: "rv-hall-pins-h", text: C.RESOURCES }), e.pinsList);
		e.sub = h("div", { class: "rv-hall-subhead" }, e.timer, e.rule, e.pins);
		// The room's "How this room works" card and its question of the day, before the log in
		// reading order. Focus stays in the composer.
		e.intro = h("div", { class: "rv-hall-intro" });
		e.banners = h("div", { class: "rv-hall-banners", role: "status" });
		e.log = h("div", { class: "rv-hall-log", role: "log", "aria-live": "polite", "aria-relevant": "additions", tabindex: "0", "data-no-obf": "", "aria-label": C.NAME });
		e.pendingBox = h("div", { class: "rv-hall-pending" });
		e.scroll = h("div", { class: "rv-hall-scroll" }, e.log);
		e.pill = h("button", { type: "button", class: "rv-hall-pill", hidden: true, onclick: () => (S.bufs.get(S.focus) || {}).detached ? ctx.jumpLatest() : scrollBottom(true) });
		e.typing = h("div", { class: "rv-hall-typing", "aria-hidden": "true" });
		e.tool = h("div", { class: "rv-hall-tools", role: "toolbar", "aria-label": C.NAME });
		// In the flow just above the composer, so a toast never covers a message.
		e.toast = h("div", { class: "rv-hall-toast", role: "status", hidden: true });
		// The timer, pins and room card share one block that gives up its height before the log does
		// (it scrolls on its own in short windows and at high zoom), so neither ever covers the other.
		e.top = h("div", { class: "rv-hall-top" }, e.sub, e.intro);
		e.main = h("section", { class: "rv-hall-main" }, e.head, e.banners, e.top, h("div", { class: "rv-hall-logwrap" }, e.scroll, e.pill), e.typing, e.toast, ctx.composer);

		e.drawer = h("aside", { class: "rv-hall-drawer", hidden: true, "aria-label": C.MOD.title });
		root.append(e.rail, e.main, e.drawer);

		e.scroll.addEventListener("scroll", onScroll, { passive: true });
		e.log.addEventListener("keydown", onLogKey);
		e.log.addEventListener("mouseover", (ev) => { const r = ev.target.closest && ev.target.closest(".rv-hall-row[data-id]"); if (r && r !== toolRow) showTools(r); });
		e.log.addEventListener("focusin", (ev) => { const r = ev.target.closest && ev.target.closest(".rv-hall-row[data-id]"); if (r && r !== toolRow) showTools(r); });
		els = e;
		return e;
	}

	function rail() {
		if (!els) return;
		const me = S.me || {};
		els.me.textContent = "";
		els.me.append(h("span", { class: "rv-hall-av", "aria-hidden": "true", style: "background:hsl(" + hue(me.u || "") + " 45% 32%)", text: monogram(me.h || "?") }), h("span", { class: "rv-hall-me-h", text: me.h || "" }));
		els.me.setAttribute("aria-label", (me.h || "") + ": " + C.CHANGE_NAME);
		els.density.textContent = C.DENSITY + ": " + (root.dataset.density === "compact" ? C.COMPACT : C.COZY);
		els.rooms.textContent = "";
		els.select.textContent = "";
		const roomBtn = (r) => {
			const cur = r.id === S.focus;
			const un = store.unread(r);
			const running = r.tmr && r.tmr.ph !== "idle";
			// The focus count has a timer glyph and says what it is, so it can't be read as unread.
			const focus = running ? h("span", { class: "rv-hall-rfocus", "data-ph": r.tmr.ph, title: C.RAIL_FOCUS(r.tmr.n) }, icon("focus"), r.tmr.n ? h("span", { "aria-hidden": "true", text: String(r.tmr.n) }) : null, h("span", { class: "rv-hall-sr", text: C.RAIL_FOCUS(r.tmr.n) })) : null;
			const ment = r.ment > 0 && !cur ? h("span", { class: "rv-hall-ment" }, h("span", { "aria-hidden": "true", text: r.ment > 99 ? "99+" : String(r.ment) }), h("span", { class: "rv-hall-sr", text: C.MENTIONED + ": " + r.ment })) : null;
			return h("button", { type: "button", class: "rv-hall-room" + (un ? " is-unread" : "") + (r.retired ? " is-retired" : ""), "aria-current": cur ? "true" : null, title: r.retired ? r.name + " (" + C.RETIRED_TAG + ")" : null, onclick: () => ctx.join(r.id) },
				h("span", { class: "rv-hall-room-name", text: r.name }),
				r.retired ? h("span", { class: "rv-hall-sr", text: " (" + C.RETIRED_TAG + ")" }) : null,
				focus, ment || (un ? h("span", { class: "rv-hall-dot", "aria-hidden": "true" }) : null));
		};
		const live = S.rooms.filter((r) => !r.retired), old = S.rooms.filter((r) => r.retired);
		for (const r of live) els.rooms.append(h("li", null, roomBtn(r)));
		// Staff only: the alpha rooms, kept for evidence, fold under one toggle.
		if (old.length) {
			const open = archivedOpen || old.some((r) => r.id === S.focus);
			const sub = h("ul", { class: "rv-hall-rooms rv-hall-archived", id: "rv-hall-archived", hidden: !open }, old.map((r) => h("li", null, roomBtn(r))));
			const t = h("button", { type: "button", class: "rv-hall-room rv-hall-archtoggle", "aria-expanded": open ? "true" : "false", "aria-controls": "rv-hall-archived", title: C.ARCHIVED_HINT, onclick: () => { archivedOpen = !open; rail(); } }, icon("chevron"), h("span", { class: "rv-hall-room-name", text: C.ARCHIVED(old.length) }));
			els.rooms.append(h("li", null, t, sub));
		}
		for (const r of [...live, ...old]) {
			const cur = r.id === S.focus;
			const un = store.unread(r);
			const running = r.tmr && r.tmr.ph !== "idle";
			const name = r.name + (r.retired ? " (" + C.RETIRED_TAG + ")" : "");
			els.select.append(h("option", { value: r.id, text: name + (running ? " \u00B7 " + C.RAIL_FOCUS(r.tmr.n) : "") + (r.ment > 0 && !cur ? " (" + r.ment + ")" : un ? " *" : "") }));
		}
		els.select.value = S.focus;
	}

	function header() {
		if (!els) return;
		const r = store.room(S.focus);
		els.title.textContent = r ? "# " + r.name : "";
		els.topic.textContent = r ? r.topic : "";
		els.topic.title = r ? r.topic : "";
		els.here.textContent = r && r.here ? C.HERE(r.here) : "";
		els.here.hidden = !(r && r.here);
		els.slow.textContent = r && r.slow ? C.SLOW(r.slow) : "";
		els.slow.hidden = !(r && r.slow);
		els.lock.hidden = !(r && r.locked);
		els.rule.textContent = r ? (r.retired ? C.RETIRED : r.rule) : "";
		els.rule.hidden = !els.rule.textContent;
		els.pollBtn.hidden = !(r && r.polls && !store.blockReason(r.id) && !(store.checkIn(r.id)));
		const me = S.me || {};
		els.invBtn.hidden = !(r && r.inv && !r.locked && !me.probation && !me.sanction && !store.lockdownLevel() && S.mode !== "readonly");
		timer();
		pins();
		intro();
		els.shield.hidden = !store.staff() && !ctx.ownerKey();
		const q = S.modq || {};
		els.modq.textContent = q.open ? String(q.open > 99 ? "99+" : q.open) : "";
		els.modq.hidden = !q.open;
		els.modq.dataset.urgent = q.urgent ? "1" : "";
	}

	// Pinned resources: Re:Vault tools open in a Re:Vault tab, staff links open like any link.
	// Both only rebuild when what they show changed, so a keyboard user's focus inside them survives
	// the header's frequent repaints (here counts, timer ticks).
	let pinsKey = "", introKey = "";
	function pins() {
		const r = store.room(S.focus);
		const list = r && !r.retired ? r.pins || [] : [];
		const key = S.focus + JSON.stringify(list);
		if (key === pinsKey) return;
		pinsKey = key;
		els.pins.hidden = !list.length;
		els.pinsList.textContent = "";
		for (const p of list) {
			const a = p.k === "tool"
				? h("a", { class: "rv-hall-pin", href: "/tools/" + p.id, "aria-label": C.OPEN_TOOL(p.label), onclick: (ev) => { ev.preventDefault(); ctx.openTool(p.id, p.label); } }, icon("tool"), h("span", { text: p.label }))
				: h("a", { class: "rv-hall-pin", href: p.url, rel: "noopener noreferrer nofollow", "data-ext": "" }, icon("link"), h("span", { text: p.label }));
			els.pinsList.append(h("li", null, a));
		}
	}

	// "How this room works" (until dismissed on this device) and the question of the day.
	function intro() {
		const r = store.room(S.focus);
		const q0 = r && r.qotd;
		const showQ = !!(q0 && q0.st === 0 && !ctx.qotdHidden(q0.id));
		const key = r ? r.id + "|" + (C.HOW[r.id] && !ctx.howSeen(r.id) ? 1 : 0) + "|" + (showQ ? q0.id + ":" + q0.b : "") : "";
		if (key === introKey) return;
		introKey = key;
		// Keep the messages near the composer where they are while the card comes and goes.
		const sc = els.scroll, fromBottom = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
		els.intro.textContent = "";
		if (!r) { els.intro.hidden = true; return; }
		const how = C.HOW[r.id];

		if (how && !ctx.howSeen(r.id)) {
			const hid = "rv-hall-how-" + r.id;
			els.intro.append(h("section", { class: "rv-hall-how", "aria-labelledby": hid },
				h("h2", { id: hid, class: "rv-hall-how-h", text: C.HOW_TITLE }),
				h("ul", null, how.map((t) => h("li", { text: t }))),
				h("button", { type: "button", class: "rv-hall-link rv-hall-how-x", text: C.HOW_DISMISS, "aria-label": C.HOW_DISMISS + ": " + C.HOW_TITLE, onclick: () => { ctx.howDismiss(r.id); intro(); } })));
		}
		const q = r.qotd;
		if (showQ) {
			const qid = "rv-hall-qotd-" + r.id;
			// Hidden on this device until the next question, so it doesn't hold a phone's screen all day.
			els.intro.append(h("section", { class: "rv-hall-qotd", "aria-labelledby": qid },
				h("h2", { id: qid, class: "rv-hall-how-h" }, icon("pin"), h("span", { text: C.QOTD })),
				h("button", { type: "button", class: "rv-hall-link rv-hall-how-x", text: C.QOTD_HIDE, "aria-label": C.QOTD_HIDE + ": " + C.QOTD, onclick: () => { ctx.qotdHide(q.id); intro(); } }),
				h("div", { class: "rv-hall-b" }, toDom(parse(q.b))),
				h("p", { class: "rv-hall-muted" }, q.a.h + " " + String.fromCharCode(183) + " ", h("time", { datetime: new Date(q.at).toISOString(), text: fmtTime(q.at) }))));
		}
		els.intro.hidden = !els.intro.childNodes.length;
		if (sc.scrollHeight > sc.clientHeight) sc.scrollTop = sc.scrollHeight - sc.clientHeight - fromBottom;
	}

	// The focus timer strip. Called every second while a timer runs (text only, nothing re-laid out).
	function timer() {
		if (!els) return;
		const r = store.room(S.focus);
		const t = r && r.tmr;
		const now = store.serverNow();
		if (!t) {
			// Hangout: when the busiest room breaks next, so people meet at the break.
			const b = r && r.id === "hangout" ? store.busiest() : null;
			const show = !!(b && b.tmr.ph === "focus" && b.tmr.end > now);
			els.timer.hidden = !show;
			els.soundWrap.hidden = true;
			if (show) {
				els.timer.dataset.ph = "next";
				els.tlabel.textContent = C.NEXT_BREAK(Math.ceil((b.tmr.end - now) / 60000));
				els.tcount.textContent = b.name + ": " + C.FOCUSING(b.tmr.n);
				els.ring.style.removeProperty("--hall-p");
				els.tbtn.hidden = true; els.tstop.hidden = true;
			}
			return;
		}
		els.timer.hidden = false;
		els.timer.dataset.ph = t.ph;
		const mine = !!(S.lease && S.lease.room === r.id);
		els.soundWrap.hidden = ctx.lite() || !(t.ph === "idle" ? !store.blockReason(r.id) : true);
		els.sound.checked = ctx.soundOn();
		const can = !store.blockReason(r.id);
		if (t.ph === "idle") {
			els.tlabel.textContent = t.plan ? C.PLAN(t.plan) : "";
			els.tlabel.removeAttribute("aria-label");
			els.tcount.textContent = "";
			els.ring.style.setProperty("--hall-p", "0");
			els.tbtn.hidden = !can || !!(S.me && S.me.probation && !store.staff());
			els.tbtn.dataset.act = "start";
			els.tbtn.textContent = C.START_FOCUS(t.plan);
		} else {
			const total = Math.max(1, t.end - t.at);
			const left = Math.max(0, t.end - now);
			els.tlabel.textContent = C.PHASE[t.ph] + " " + clock(left);
			els.tlabel.setAttribute("aria-label", C.TIMER_LABEL(C.PHASE[t.ph], clock(left)));
			els.tcount.textContent = t.n ? C.FOCUSING(t.n) : "";
			// At least 3% of the ring shows, so a block that just started is still visibly running.
			els.ring.style.setProperty("--hall-p", String(Math.max(3, Math.round((1 - left / total) * 1000) / 10)));

			els.tbtn.hidden = false;
			els.tbtn.dataset.act = mine ? "leave" : "join";
			els.tbtn.textContent = mine ? C.STOP_FOCUSING : C.FOCUS_WITH;
		}
		els.tbtn.classList.toggle("rv-btn-primary", els.tbtn.dataset.act !== "leave");
		els.tstop.hidden = !(store.staff() && t.ph !== "idle");
	}

	// conn: "" | "reconnecting" | "back" | "blocked"; state banners come from the room's blockReason.
	function banners(conn) {
		if (!els) return;
		els.banners.textContent = "";
		const add = (text, kind) => els.banners.append(h("p", { class: "rv-hall-banner", "data-kind": kind || "", text }));
		if (conn === "reconnecting") add(C.RECONNECTING, "warn");
		else if (conn === "back") add(C.BACK_ONLINE, "ok");
		else if (conn === "blocked") add(C.WS_BLOCKED, "warn");
		const why = store.blockReason(S.focus);
		const s = store.sanctionActive();
		if (why === "timeout" && s) add(C.TIMED_OUT(fmtUntil(s.until, store.serverNow()), s.reason || ""), "danger");
		else if (why === "readonly") add(C.READONLY);
		else if (why === "lockdown3") add(C.LOCKDOWN_ALL);
		else if (why === "lockdown") add(C.LOCKDOWN_NEW);
		else if (why === "locked") add(C.LOCKED);
	}

	// --- rows ---------------------------------------------------------------------------------
	function actionsFor(m) {
		const me = S.me || {}, own = m.a.u === me.u, sys = m.a.s === 3, staff = store.staff();
		const out = [];
		if (m.st === 0) {
			if (!store.blockReason(m.room)) out.push("reply");
			if (store.canReact(m.room)) out.push("react");
			if (own && store.canEdit(m)) out.push("edit");
			if (own) out.push("del");
			if (!own && !sys) out.push("report");
		}
		// An author-deleted message stays reportable until the server scrubs its body (7 days after
		// the delete; m.at is never later than that, so this ends first).
		if (m.st === 1 && !own && !sys && store.serverNow() - m.at < REPORT_DELETED_MS) out.push("report");
		// Still waiting for a moderator: the author can take it back.
		if (m.st === 4 && own) out.push("del");
		if (!own && !sys && m.a.s === 0 && !staff) out.push(S.blocks.has(m.a.u) ? "unblock" : "block");
		if (staff && !own && !sys) {
			if (m.st === 4) out.push("approve");
			if (m.st === 0 || m.st === 3 || m.st === 4) out.push("remove");
			if (m.st === 2 || m.st === 3) out.push("restore");
			if (m.a.s === 0 || me.staff === 2) out.push("timeout", "ban");
			out.push("user");
		}
		return out;
	}

	function chips(m) {
		const box = h("div", { class: "rv-hall-x" });
		let any = false;
		m.x.forEach((n, e) => {
			if (n <= 0) return;
			any = true;
			const mine = store.mineHas(m.id, e);
			box.append(h("button", { type: "button", class: "rv-hall-chip", "aria-pressed": mine ? "true" : "false", "aria-label": C.REACT_LABEL(C.REACTION_NAMES[e], n), title: C.REACTION_NAMES[e], onclick: () => ctx.act("toggle", m, e) }, icon("rx" + e), h("span", { "aria-hidden": "true", text: String(n) })));
		});
		if (!any) return null;
		if (m.st === 0 && store.canReact(m.room)) box.append(h("button", { type: "button", class: "rv-hall-chip rv-hall-chip-add", "aria-label": C.T.more, title: C.T.more, onclick: (ev) => openPicker(m, ev.currentTarget) }, icon("plus")));
		return box;
	}

	function body(m) {
		const staffView = store.staff();
		// Held for a moderator: the author and staff see the words with a plain note under them.
		if ((m.st === 4 && m.b) || (m.st === 5 && staffView && m.b)) {
			const own = S.me && m.a.u === S.me.u;
			const note = m.st === 5 ? C.HELD_GONE_STAFF : own && !staffView ? C.HELD_OWN : C.HELD_STAFF;
			return h("div", { class: "rv-hall-b is-held" }, toDom(parse(m.b)), h("p", { class: "rv-hall-held", text: note }));
		}
		if (m.st !== 0) {
			const b = h("div", { class: "rv-hall-b is-gone" }, h("em", { text: C.ST_LABEL[m.st] || C.ST_LABEL[1] }));
			if (staffView && m.b) b.append(h("div", { class: "rv-hall-staff-b" }, toDom(parse(m.b))));
			return b;
		}
		if (m.k === 1) return pollEl(m);
		const b = h("div", { class: "rv-hall-b" }, toDom(parse(m.b)));
		if (m.ed) b.append(" ", h("span", { class: "rv-hall-edited", title: fmtFull(m.ed), text: C.EDITED }));
		return b;
	}

	// A poll: the question, one button per option with its share, and counts only.
	function pollEl(m) {
		const p = m.poll;
		if (!p) return h("div", { class: "rv-hall-b is-gone" }, h("em", { text: C.POLL_GONE }));
		const now = store.serverNow();
		const open = p.until > now;
		const mine = store.myVote(m.id);
		const total = p.pv.reduce((a, b) => a + b, 0);
		const can = open && !store.blockReason(m.room);
		const box = h("div", { class: "rv-hall-b rv-hall-poll", role: "group", "aria-label": C.POLL + ": " + m.b },
			h("div", { class: "rv-hall-poll-q" }, icon("poll"), h("strong", { text: m.b })));
		p.o.forEach((o, i) => {
			const pct = total ? Math.round((p.pv[i] / total) * 100) : 0;
			box.append(h("button", {
				type: "button", class: "rv-hall-poll-o" + (mine === i ? " is-mine" : ""), "aria-pressed": mine === i ? "true" : "false", disabled: !can,
				style: "--hall-w:" + pct + "%", onclick: () => ctx.act("vote", m, i),
			}, h("span", { class: "rv-hall-poll-t", text: o }), p.few ? null : h("span", { class: "rv-hall-poll-n", text: pct + "%" })));
		});
		box.append(h("p", { class: "rv-hall-poll-meta" },
			(p.few ? C.POLL_FEW : C.POLL_VOTES(total)) + " \u00B7 " + (open ? C.POLL_CLOSES(fmtTime(p.until)) : C.POLL_CLOSED) + " \u00B7 " + C.POLL_PRIVATE,
			can && mine >= 0 ? h("button", { type: "button", class: "rv-hall-link", text: C.POLL_CLEAR, onclick: () => ctx.act("vote", m, -1) }) : null));
		return box;
	}

	function rowEl(m, prev) {
		const me = S.me || {};
		if (S.blocks.has(m.a.u) && !revealed.has(m.id)) {
			return h("div", { class: "rv-hall-row is-blocked", "data-id": m.id, tabindex: "-1" },
				h("span", { text: C.BLOCKED_ROW + " " }),
				h("button", { type: "button", class: "rv-hall-link", text: C.SHOW, onclick: () => { revealed.add(m.id); update(m); } }));
		}
		const cont = groupable(prev, m);
		const row = h("div", { class: "rv-hall-row" + (cont ? " is-cont" : "") + (me.u && m.mn.includes(me.u) ? " is-ment" : "") + (m.a.u === me.u ? " is-own" : ""), "data-id": m.id, tabindex: "-1" });
		if (m.rp) {
			row.append(h("button", { type: "button", class: "rv-hall-rp", onclick: () => scrollTo(m.rp.id) },
				icon("reply"),
				h("span", { class: "rv-hall-rp-h", text: C.REPLY_TO_PREVIEW(m.rp.h) + ": " }),
				h("span", { class: "rv-hall-rp-ex", text: plain(m.rp.ex) || C.REPLY_GONE })));
		}
		const handle = m.a.s === 3 ? m.a.h || "Re:Vault Team" : m.a.h;
		if (!cont) row.append(h("span", { class: "rv-hall-av", "aria-hidden": "true", style: "background:hsl(" + hue(m.a.u) + " 45% 32%)", text: monogram(handle) }));
		const main = h("div", { class: "rv-hall-mainc" });
		if (!cont) {
			const badge = badgeFor(m.a.s), fl = m.a.s === 3 ? null : flairOf(m.a.f);
			main.append(h("div", { class: "rv-hall-meta" },
				m.a.s === 3 ? h("span", { class: "rv-hall-h", text: handle }) : h("button", { type: "button", class: "rv-hall-h", "data-c": fl && fl.c, text: handle, "aria-label": C.VIEW_PROFILE + ": " + handle, onclick: () => ctx.act("card", m) }),
				badge ? h("span", { class: "rv-hall-badge", "data-s": m.a.s, text: badge }) : null,
				fl ? h("span", { class: "rv-hall-sup", "data-b": fl.b }, fl.g ? h("span", { "aria-hidden": "true", text: fl.g + " " }) : null, fl.name) : null,
				h("time", { class: "rv-hall-time", datetime: new Date(m.at).toISOString(), title: fmtFull(m.at), text: fmtTime(m.at) })));
		} else {
			row.append(h("time", { class: "rv-hall-time rv-hall-gutter", datetime: new Date(m.at).toISOString(), title: fmtFull(m.at), text: fmtTime(m.at) }));
		}
		main.append(body(m));
		const x = chips(m);
		if (x) main.append(x);
		row.append(main);
		return row;
	}

	function pendingEl(p) {
		return h("div", { class: "rv-hall-row is-own is-pending", "data-cid": p.cid },
			p.rpH ? h("div", { class: "rv-hall-rp" }, icon("reply"), h("span", { class: "rv-hall-rp-h", text: C.REPLY_TO_PREVIEW(p.rpH) })) : null,
			h("div", { class: "rv-hall-mainc" },
				h("div", { class: "rv-hall-b" }, toDom(parse(p.b))),
				h("div", { class: "rv-hall-sending", text: p.state === "queued" ? C.QUEUED : C.SENDING })));
	}

	// --- log ----------------------------------------------------------------------------------
	function prevOf(list, i) { return i > 0 ? list[i - 1] : null; }

	// Only rows arriving while you read at the bottom are announced. A full repaint (a room switch,
	// history, a moderator hiding something) would otherwise read the whole log out again.
	let liveT = 0;
	function quiet() {
		els.log.setAttribute("aria-live", "off");
		if (liveT) cancelAnimationFrame(liveT);
		liveT = requestAnimationFrame(() => { liveT = requestAnimationFrame(() => { liveT = 0; if (els) els.log.setAttribute("aria-live", atBottom() ? "polite" : "off"); }); });
	}

	function log() {
		if (!els) return;
		hideTools();
		quiet();
		const b = S.bufs.get(S.focus);
		const list = store.list(S.focus);
		const wasBottom = atBottom();
		const anchor = firstVisible();
		if (markRoom !== S.focus) { markRoom = S.focus; const r = store.room(S.focus); mark = r ? r.read : 0; newCount = 0; }
		rowEls.clear();
		els.log.textContent = "";
		if (b && b.more) els.log.append(h("button", { type: "button", class: "rv-hall-older", text: C.LOAD_OLDER, onclick: ctx.more }));
		if (!b || !b.loaded) els.log.append(h("p", { class: "rv-hall-empty", text: C.LOADING }));
		else if (!list.length && !store.pendingIn(S.focus).length) { const rr = store.room(S.focus); els.log.append(h("p", { class: "rv-hall-empty", text: (rr && C.EMPTY_BY_KIND[rr.kind]) || C.EMPTY_ROOM })); }
		let divider = false;
		list.forEach((m, i) => {
			const prev = prevOf(list, i);
			if (!prev || dayKey(prev.at) !== dayKey(m.at)) els.log.append(h("div", { class: "rv-hall-day", role: "separator", text: dayLabel(m.at) }));
			if (!divider && mark && m.id > mark && S.me && m.a.u !== S.me.u) { divider = true; els.log.append(h("div", { class: "rv-hall-new", role: "separator", text: C.NEW_DIVIDER })); }
			const el = rowEl(m, divider && els.log.lastChild.className === "rv-hall-new" ? null : prev);
			rowEls.set(m.id, el);
			els.log.append(el);
		});
		lastFirst = list.length ? list[0].id : 0;
		els.log.append(els.pendingBox);
		pending();
		if (anchor && !wasBottom && rowEls.has(anchor.id)) {
			const el = rowEls.get(anchor.id);
			els.scroll.scrollTop += el.getBoundingClientRect().top - anchor.top;
		} else scrollBottom(false);
		pill();
		typing();
	}

	// A live row at the end (the common case), without re-rendering the log.
	function append(m) {
		if (!els || m.room !== S.focus) return;
		const list = store.list(S.focus);
		const i = list.findIndex((x) => x.id === m.id);
		if (i === -1) return;
		// A row already on screen came again (a held message that was approved): repaint it in place.
		if (rowEls.has(m.id)) { update(m); return; }
		if (i !== list.length - 1 || list[0].id !== lastFirst || !rowEls.size) { log(); return; }
		const wasBottom = atBottom();
		const prev = prevOf(list, i);
		const empty = els.log.querySelector(".rv-hall-empty");
		if (empty) empty.remove();
		if (!prev || dayKey(prev.at) !== dayKey(m.at)) els.log.insertBefore(h("div", { class: "rv-hall-day", role: "separator", text: dayLabel(m.at) }), els.pendingBox);
		const el = rowEl(m, prev);
		rowEls.set(m.id, el);
		els.log.insertBefore(el, els.pendingBox);
		if (wasBottom) scrollBottom(false);
		else if (!S.me || m.a.u !== S.me.u) { newCount++; pill(); }
	}

	function update(m) {
		const old = rowEls.get(m.id);
		if (!old || !els) return;
		quiet(); // a reaction or an edit isn't news: don't read the message out again
		const list = store.list(m.room);
		const i = list.findIndex((x) => x.id === m.id);
		const el = rowEl(m, i > 0 && !(old.previousSibling && old.previousSibling.className === "rv-hall-new") ? list[i - 1] : null);
		if (toolRow === old) hideTools();
		old.replaceWith(el);
		rowEls.set(m.id, el);
	}

	function pending() {
		if (!els) return;
		els.pendingBox.textContent = "";
		for (const p of store.pendingIn(S.focus)) els.pendingBox.append(pendingEl(p));
	}

	function typing() {
		if (!els) return;
		els.typing.textContent = ctx.lite() ? "" : store.typingLine(S.focus, C.TYPING);
	}

	function pill() {
		if (!els) return;
		const b = S.bufs.get(S.focus);
		const detached = !!(b && b.detached);
		els.pill.hidden = !detached && newCount === 0;
		els.pill.textContent = detached ? C.JUMP_LATEST : C.NEW_PILL(newCount);
	}

	function atBottom() { return !els || els.scroll.scrollHeight - els.scroll.scrollTop - els.scroll.clientHeight < 48; }
	function scrollBottom(smooth) {
		if (!els) return;
		els.scroll.scrollTo({ top: els.scroll.scrollHeight, behavior: smooth && !ctx.lite() ? "smooth" : "auto" });
		newCount = 0;
		pill();
	}
	function firstVisible() {
		if (!els || !rowEls.size) return null;
		const top = els.scroll.getBoundingClientRect().top;
		for (const [id, el] of rowEls) { const r = el.getBoundingClientRect(); if (r.bottom > top) return { id, top: r.top }; }
		return null;
	}
	function scrollTo(id) {
		const el = rowEls.get(id);
		if (!el) return;
		el.scrollIntoView({ block: "center", behavior: ctx.lite() ? "auto" : "smooth" });
		el.classList.add("is-flash");
		setTimeout(() => el.classList.remove("is-flash"), 1200);
		el.focus({ preventScroll: true });
	}
	let scrollT = 0;
	function onScroll() {
		if (scrollT) return;
		scrollT = requestAnimationFrame(() => {
			scrollT = 0;
			if (!els) return;
			const bottom = atBottom();
			els.log.setAttribute("aria-live", bottom ? "polite" : "off");
			if (bottom && newCount) { newCount = 0; pill(); }
			const b = S.bufs.get(S.focus);
			if (els.scroll.scrollTop < 80 && b && b.more) ctx.more();
			ctx.onScroll(bottom);
		});
	}
	function onLogKey(ev) {
		if (ev.key !== "ArrowUp" && ev.key !== "ArrowDown") return;
		if (ev.target.closest && ev.target.closest(".rv-hall-tools, .rv-hall-picker")) return;
		const rows = [...els.log.querySelectorAll(".rv-hall-row[data-id]")];
		if (!rows.length) return;
		const cur = ev.target.closest ? ev.target.closest(".rv-hall-row[data-id]") : null;
		let i = cur ? rows.indexOf(cur) + (ev.key === "ArrowUp" ? -1 : 1) : rows.length - 1;
		i = Math.max(0, Math.min(rows.length - 1, i));
		ev.preventDefault();
		rows[i].focus();
		rows[i].scrollIntoView({ block: "nearest" });
	}

	// --- toolbar and reaction picker ------------------------------------------------------------
	function showTools(row) {
		const id = Number(row.dataset.id);
		const m = store.findRow(id);
		if (!m || row.classList.contains("is-blocked")) { hideTools(); return; }
		toolRow = row;
		els.tool.textContent = "";
		for (const verb of actionsFor(m)) {
			const danger = verb === "del" || verb === "remove" || verb === "ban" || verb === "block" || verb === "report";
			els.tool.append(h("button", { type: "button", class: "rv-hall-icon-btn" + (danger ? " is-danger" : ""), "aria-label": C.T[verb], title: C.T[verb], onclick: (ev) => { if (verb === "react") openPicker(m, ev.currentTarget); else ctx.act(verb, m); } }, icon(verb)));
		}
		if (!els.tool.childNodes.length) { hideTools(); return; }
		els.tool.classList.remove("is-below");
		row.append(els.tool);
		// A row at the top edge of the log would hide its toolbar under the header: drop it below.
		if (els.tool.getBoundingClientRect().top < els.scroll.getBoundingClientRect().top) els.tool.classList.add("is-below");
	}
	function hideTools() { if (els && els.tool.parentNode) els.tool.remove(); toolRow = null; }

	function openPicker(m, anchor) {
		closePicker();
		picker = h("div", { class: "rv-hall-picker", role: "menu", "aria-label": C.T.react });
		S.reactions.forEach((_, e) => {
			picker.append(h("button", { type: "button", role: "menuitemcheckbox", "aria-checked": store.mineHas(m.id, e) ? "true" : "false", "aria-label": C.REACTION_NAMES[e], title: C.REACTION_NAMES[e], class: "rv-hall-pick", onclick: () => { closePicker(); ctx.act("toggle", m, e); } }, icon("rx" + e)));
		});
		document.body.append(picker);
		const r = anchor.getBoundingClientRect();
		const w = picker.offsetWidth, hgt = picker.offsetHeight;
		picker.style.left = Math.max(8, Math.min(window.innerWidth - w - 8, r.right - w)) + "px";
		picker.style.top = (r.top - hgt - 6 > 8 ? r.top - hgt - 6 : r.bottom + 6) + "px";
		picker.firstChild.focus();
		setTimeout(() => document.addEventListener("pointerdown", outside, true), 0);
		picker.addEventListener("keydown", (ev) => {
			if (ev.key === "Escape") { ev.stopPropagation(); closePicker(); anchor.focus(); }
			if (ev.key === "ArrowRight" || ev.key === "ArrowLeft") {
				const b = [...picker.children], i = b.indexOf(document.activeElement);
				b[(i + (ev.key === "ArrowRight" ? 1 : b.length - 1)) % b.length].focus();
			}
		});
	}
	function outside(ev) { if (picker && !picker.contains(ev.target)) closePicker(); }
	function closePicker() { if (picker) { picker.remove(); picker = null; } document.removeEventListener("pointerdown", outside, true); }

	// A private, dismissible card at the end of the log (support notices, info from the server).
	function notice(d) {
		if (!els) return;
		const links = Array.isArray(d.links) ? d.links : [];
		const box = h("div", { class: "rv-hall-notice", role: "note", "data-kind": d.kind === "support" ? "support" : "info" },
			h("strong", { text: C.NOTICE_TITLE }),
			h("p", { text: typeof d.text === "string" && d.text ? d.text : d.kind === "support" ? C.SUPPORT : "" }));
		const ok = links.map((l) => l && typeof l.label === "string" && typeof l.href === "string" ? l : null).filter(Boolean);
		if (ok.length) box.append(h("p", { class: "rv-hall-notice-links" }, ok.map((l) => noticeLink(l))));
		box.append(h("button", { type: "button", class: "rv-hall-link", text: C.DISMISS, onclick: () => box.remove() }));
		els.log.insertBefore(box, els.pendingBox);
		if (atBottom()) scrollBottom(false);
	}
	function noticeLink(l) {
		let u = null;
		try { u = new URL(l.href); } catch (e) { void e; }
		if (!u || !/^(https?|tel|sms|mailto):$/.test(u.protocol)) return h("span", { text: l.label + " " });
		const a = h("a", { href: u.href, rel: "noopener noreferrer nofollow", text: l.label });
		if (u.protocol === "http:" || u.protocol === "https:") a.setAttribute("data-ext", "");
		else a.target = "_blank"; // tel:, sms:, mailto: hand off to the device (a framed page can't navigate itself there)
		return a;
	}

	function toast(text) {
		if (!els || !text) return;
		els.toast.textContent = text;
		els.toast.hidden = false;
		clearTimeout(toastT);
		toastT = setTimeout(() => { if (els) els.toast.hidden = true; }, 4200);
	}

	function drawer() { return els ? els.drawer : null; }

	return {
		card, loading, mount, rail, header, timer, banners, log, append, update, pending, typing, pill, notice, toast,
		atBottom, scrollBottom, scrollTo, closePicker, hideTools, drawer, isMounted: () => !!els, actionsFor,
		resetMark() { markRoom = ""; },
	};
}
